import { createSelector, createSlice, PayloadAction } from '@reduxjs/toolkit';
import { streamMaximized, streamMinimized } from 'features/layout/actions';
import {
  GridPanelName,
  LayoutContentState,
  ContentData,
  ContentRef,
  ContentType,
} from 'features/layout/features/content/types';
import {
  selectLastOpenedExternalStream,
  selectLastOpenedScreenshare,
  selectScreenshareStreams,
} from 'features/layout/features/order/orderSlice';
import {
  fileOpened,
  selectContentLibraryActiveFiles,
  selectContentLibraryActiveWhiteboards,
  selectIsPreviewActive,
  selectLastContentLibraryOpenedFile,
  selectLastContentLibraryOpenedWhiteboard,
} from 'features/content-library/contentLibrarySlice';
import { findLatestOpenedContent } from 'features/layout/features/content/utils/findLatestOpenedContent';
import { RootState } from 'store/store';
import { selectContentWidth } from 'features/layout/features/contentDimensions/contentDimensionsSlice';
import { signalingContentUnlocked } from 'features/layout/features/content/actions';
import { BulkCloseContentScope } from 'features/layout/types';
import {
  selectCanManageAllScreenshares,
  selectPermissionsState,
} from 'features/permissions/permissionsSlice';
import { selectIsSystemSession } from 'features/application/selectors';
import { selectLocalUser } from 'features/users/usersSlice';
import { refinePermissions } from 'features/permissions/utils/refinePermissions';
import { PermissionTypes } from 'features/permissions/types';

export const initialState: LayoutContentState = {
  maximizedContent: null,
  maximizedStream: null,
  previouslyMaximizedStream: null,
  expectScreenshareOnStage: false,
  gridPanel: null,
  gridPanelMaximized: false,
  availableTabs: {
    screenshare: false,
    whiteboard: false,
    contentLibrary: false,
    external: false,
  },
  readyPanels: {
    screenshare: true,
    whiteboard: true,
    contentLibrary: true,
    external: true,
  },
  lockedContent: null,
};

const contentLibraryTabsPanels: GridPanelName[] = ['whiteboard', 'contentLibrary'];

const layoutContentSlice = createSlice({
  name: 'layoutContent',
  initialState,
  reducers: {
    gridPanelFullscreenChanged(state, action: PayloadAction<boolean>) {
      state.gridPanelMaximized = action.payload;
    },
    gridPanelOpened(state, action: PayloadAction<GridPanelName>) {
      state.gridPanel = action.payload;
      state.availableTabs[action.payload] = true;

      if (!contentLibraryTabsPanels.includes(action.payload)) {
        state.readyPanels.contentLibrary = false;
      }
    },
    gridPanelTabChanged(state, action: PayloadAction<GridPanelName>) {
      state.gridPanel = action.payload;
      state.availableTabs[action.payload] = true;
    },
    gridPanelClosed(state) {
      if (state.gridPanel) {
        const availableTabs = Object.keys(state.availableTabs);

        for (const tab of availableTabs) {
          state.availableTabs[tab as GridPanelName] = false;
          state.gridPanel = null;
        }
      }
    },
    gridPanelVoided(state, action: PayloadAction<GridPanelName>) {
      state.availableTabs[action.payload] = false;
    },
    gridPanelInitialized(
      state,
      action: PayloadAction<{
        availableTabs: Record<GridPanelName, boolean>;
        activeTab: GridPanelName;
      }>
    ) {
      state.gridPanel = action.payload.activeTab;
      state.availableTabs = action.payload.availableTabs;
    },
    gridPanelReadyChanged(state, action: PayloadAction<{ type: GridPanelName; ready: boolean }>) {
      state.readyPanels[action.payload.type] = action.payload.ready;
    },
    gridPanelAvailableTabChanged(
      state,
      action: PayloadAction<{ type: GridPanelName; active: boolean }>
    ) {
      state.availableTabs[action.payload.type] = action.payload.active;
    },
    lockedContentChanged(state, action: PayloadAction<ContentData | null>) {
      state.lockedContent = action.payload;
    },
    gridPanelTabClosed(state, action: PayloadAction<GridPanelName>) {
      state.availableTabs = {
        ...state.availableTabs,
        [action.payload]: false,
      };
    },
  },
  extraReducers: (builder) => {
    builder.addCase(streamMaximized, (state, action) => {
      state.maximizedContent = 'stream';
      state.maximizedStream = action.payload.source;
    });
    builder.addCase(streamMinimized, (state) => {
      state.previouslyMaximizedStream = state.maximizedStream;

      state.maximizedContent = null;
      state.maximizedStream = null;
    });
    builder.addCase(fileOpened, (state) => {
      // @TODO refactor if we don't need to update content-library types together
      contentLibraryTabsPanels.forEach((panel) => {
        state.readyPanels[panel] = true;
      });
    });
    builder.addCase(signalingContentUnlocked, (state) => {
      state.lockedContent = null;
    });
  },
});

export const {
  gridPanelFullscreenChanged,
  gridPanelOpened,
  gridPanelTabChanged,
  gridPanelClosed,
  gridPanelVoided,
  gridPanelTabClosed,
  gridPanelInitialized,
  gridPanelAvailableTabChanged,
  gridPanelReadyChanged,
  lockedContentChanged,
} = layoutContentSlice.actions;

export const selectMaximizedStream = (state: RootState) => state.layout.content.maximizedStream;
export const selectPreviouslyMaximizedStream = (state: RootState) =>
  state.layout.content.previouslyMaximizedStream;

export const selectExpectScreenshareOnStage = (state: RootState) =>
  state.layout.content.expectScreenshareOnStage;

export const selectMaximizedContent = (state: RootState) => {
  if (
    state.layout.content.maximizedContent === 'stream' &&
    state.layout.contentDimensions.width !== 1
  ) {
    return null;
  }

  return state.layout.content.maximizedContent;
};

export const selectGridPanel = (state: RootState) => state.layout.content.gridPanel;

export const selectAvailableTabs = (state: RootState) => state.layout.content.availableTabs;

export const selectAvailableTabsCount = (state: RootState) =>
  Object.values(state.layout.content.availableTabs).filter(Boolean).length;

export const selectLockedContent = (state: RootState) => state.layout.content.lockedContent;

export const selectLockedFile = createSelector(
  [selectLockedContent, selectContentLibraryActiveFiles],
  (lockedContent, files) =>
    lockedContent?.type === 'file' ? files.find((file) => file.id === lockedContent.id) : undefined
);

export const selectLockedWhiteboard = createSelector(
  [selectLockedContent, selectContentLibraryActiveWhiteboards],
  (lockedContent, whiteboards) =>
    lockedContent?.type === 'whiteboard'
      ? whiteboards.find((whiteboard) => whiteboard.id === lockedContent.id)
      : undefined
);

export const selectLockedScreenshare = createSelector(
  [selectLockedContent, selectScreenshareStreams],
  (lockedContent, streams) =>
    lockedContent?.type === 'screenshare'
      ? streams.find((stream) => stream.feedId === lockedContent.id)
      : undefined
);

export const selectIsContentLocked = createSelector(
  [selectLockedContent],
  (lockedContent) => lockedContent !== null
);

export const selectIsLockedContentReadOnly = createSelector(
  [selectIsContentLocked, selectPermissionsState, selectLocalUser],
  (isContentLocked, permissionsState, localUser) => {
    const canLockContent = refinePermissions(PermissionTypes.lockContent, {
      permissionsState,
      localUser,
    });

    return isContentLocked && !canLockContent;
  }
);

export const selectShouldShowContentSelector = createSelector(
  [selectIsLockedContentReadOnly, selectIsPreviewActive],
  (shouldHideTabs, previewActive) => !(shouldHideTabs || previewActive)
);

export const selectShowGridPanelTabs = createSelector(
  [selectAvailableTabsCount, selectIsLockedContentReadOnly, selectIsPreviewActive],
  (availableTabsCount, shouldHideTabs, isPreviewActive) =>
    availableTabsCount > 1 && !shouldHideTabs && !isPreviewActive
);

export const selectCanSeeContentBadges = createSelector(
  [selectPermissionsState, selectLocalUser],
  (permissionsState, localUser) =>
    refinePermissions(PermissionTypes.seeContentBadges, { permissionsState, localUser })
);

export const selectAllOpenContent = createSelector(
  [
    selectScreenshareStreams,
    selectContentLibraryActiveWhiteboards,
    selectContentLibraryActiveFiles,
  ],
  (screenshares, whiteboards, files): ContentRef[] => [
    ...screenshares.map((stream) => ({ type: 'screenshare' as ContentType, id: stream.userId })),
    ...whiteboards.map((wb) => ({ type: 'whiteboard' as ContentType, id: wb.id })),
    ...files.map((file) => ({ type: 'file' as ContentType, id: file.id })),
  ]
);

export const selectContentByScope = (
  state: RootState,
  scope: BulkCloseContentScope
): ContentRef[] => {
  if (scope === 'all') {
    return selectAllOpenContent(state);
  }

  if (scope === 'whiteboards') {
    return selectContentLibraryActiveWhiteboards(state).map((item) => ({
      type: 'whiteboard',
      id: item.id,
    }));
  }

  if (scope === 'files') {
    return selectContentLibraryActiveFiles(state).map((item) => ({
      type: 'file',
      id: item.id,
    }));
  }

  if (scope === 'screenshares') {
    return selectScreenshareStreams(state).map((item) => ({
      type: 'screenshare',
      id: item.userId,
    }));
  }

  return [];
};

export const selectContentCountByScope = (
  state: RootState,
  scope: BulkCloseContentScope
): number => {
  if (scope === 'all') {
    return selectAllOpenContent(state).length;
  }

  if (scope === 'whiteboards') {
    return selectContentLibraryActiveWhiteboards(state).length;
  }

  if (scope === 'files') {
    return selectContentLibraryActiveFiles(state).length;
  }

  if (scope === 'screenshares') {
    return selectScreenshareStreams(state).length;
  }

  return 0;
};

export const selectCanCloseAllContent = createSelector(
  [selectCanManageAllScreenshares, selectPermissionsState, selectLocalUser, selectIsSystemSession],
  (canManageAllScreenshares, permissionsState, localUser, isSystemSession) => {
    const checkOptions = { permissionsState, localUser, isSystemSession };

    return (
      canManageAllScreenshares &&
      refinePermissions(PermissionTypes.editWhiteboard, checkOptions) &&
      refinePermissions(PermissionTypes.presentContentLibrary, checkOptions)
    );
  }
);

export const selectGridPanelMaximized = (state: RootState) =>
  state.layout.content.gridPanelMaximized;

export const selectGridPanelCollapsed = createSelector(
  [selectMaximizedContent, selectContentWidth],
  (maximizedContent, contentWidth) => maximizedContent === 'stream' && contentWidth === 1
);

export const selectGridPanelReady = (state: RootState, type: GridPanelName) =>
  state.layout.content.readyPanels[type];

export const selectLastOpenedContent = createSelector(
  [
    selectLastOpenedExternalStream,
    selectLastOpenedScreenshare,
    selectLastContentLibraryOpenedFile,
    selectLastContentLibraryOpenedWhiteboard,
  ],
  (lastOpenedExternalStream, lastOpenedScreenshare, lastOpenedFile, lastOpenedWhiteboard) =>
    findLatestOpenedContent(
      lastOpenedExternalStream,
      lastOpenedScreenshare,
      lastOpenedFile,
      lastOpenedWhiteboard
    )
);

export const selectCurrentOpenedContent = createSelector(
  [
    selectGridPanel,
    (state: RootState) => state.contentLibrary.openedFile.data,
    (state: RootState) => state.screenshare.selectedScreenshare,
  ],
  (currentGridPanel, openedFile, selectedScreenshare) => {
    if (currentGridPanel === 'contentLibrary') {
      if (openedFile) {
        return {
          type: 'file',
          id: openedFile.id,
          page: openedFile.page,
        };
      }

      return null;
    }

    if (currentGridPanel === 'whiteboard') {
      if (openedFile) {
        return {
          type: 'whiteboard',
          id: openedFile.id,
        };
      }

      return null;
    }

    if (selectedScreenshare && currentGridPanel === 'screenshare') {
      return {
        type: 'screenshare',
        id: selectedScreenshare.feedId,
      };
    }

    return null;
  }
);

export const selectCurrentContentId = createSelector(
  [selectCurrentOpenedContent],
  (contentData) => contentData?.id
);

export default layoutContentSlice.reducer;
